/* eslint-disable no-console */
import { LOG_LEVEL } from '@/config';

/**
 * Signature of a logging function.
 *
 * @param message - The primary message to log (can be of any type).
 * @param optionalParams - Additional parameters to include in the log output.
 */
export interface LogFn {
  (message?: unknown, ...optionalParams: unknown[]): void;
}

/**
 * Defines the core logging methods for a logger.
 */
export interface Logger {
  log: LogFn;
  warn: LogFn;
  error: LogFn;
}

/**
 * Supported log levels.
 */
export type LogLevel = 'log' | 'warn' | 'error';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NO_OP: LogFn = (_message?: unknown, ..._optionalParams: unknown[]) => {};

/**
 * A logger implementation that outputs messages to the browser console.
 *
 * Allows filtering log messages based on the specified log level.
 */
export class ConsoleLogger implements Logger {
  readonly log: LogFn;
  readonly warn: LogFn;
  readonly error: LogFn;

  /**
   * Creates a new ConsoleLogger instance.
   *
   * @param options - Optional configuration options.
   * @param options.level - The minimum log level to output. Defaults to the value of `LOG_LEVEL`.
   */
  constructor(options?: { level?: LogLevel }) {
    const { level } = options || {};

    // Always log errors
    this.error = console.error.bind(console);

    // Disable other methods if log level is too low
    if (level === 'error') {
      this.warn = NO_OP;
      this.log = NO_OP;
      return;
    }

    this.warn = console.warn.bind(console);

    if (level === 'warn') {
      this.log = NO_OP;
      return;
    }

    this.log = console.log.bind(console);
  }
}

/**
 * The default logger instance, using the log level from the application configuration.
 */
export const logger = new ConsoleLogger({ level: LOG_LEVEL });
